/**
 * Document state and the save-state machine behind the status pill
 * (implementation-plan.md §4.3).
 *
 * Kept free of DOM so the wording and thresholds can be unit-tested directly.
 */

import type { ChronoDocument } from '../model/types';
import { validateDocument } from '../model/validate';

/** Past this, the pill stops being informative and starts being a warning. */
export const URGENT_AFTER_MINUTES = 10;

export type SaveState =
  | { kind: 'none' }
  /** Created in this session and never written to disk. */
  | { kind: 'never-saved'; since: Date }
  /** Read from a file and untouched since; `at` comes from meta.savedAt. */
  | { kind: 'opened'; at: Date }
  | { kind: 'saved'; at: Date }
  | { kind: 'dirty'; since: Date };

export interface DocumentState {
  doc: ChronoDocument | null;
  save: SaveState;
  /** Non-blocking: a document that fails validation still opens, so it can be fixed. */
  validationErrors: string[];
  canUndo: boolean;
  canRedo: boolean;
}

type Listener = (state: DocumentState) => void;

/**
 * Snapshots kept for undo. With a single copy on disk and no browser
 * persistence, an unrecoverable mistake is expensive — but the history itself
 * dies with the session, like everything else in the browser.
 */
const MAX_HISTORY = 100;

export function createDocumentState() {
  let state: DocumentState = {
    doc: null,
    save: { kind: 'none' },
    validationErrors: [],
    canUndo: false,
    canRedo: false,
  };
  let past: ChronoDocument[] = [];
  let future: ChronoDocument[] = [];

  const listeners = new Set<Listener>();

  const emit = () => {
    for (const listener of listeners) listener(state);
  };

  const set = (next: Partial<DocumentState>) => {
    state = { ...state, ...next, canUndo: past.length > 0, canRedo: future.length > 0 };
    emit();
  };

  const resetHistory = () => {
    past = [];
    future = [];
  };

  /** Keeps the origin of the current unsaved changes rather than resetting it. */
  const dirtySince = (now: Date): Date => (state.save.kind === 'dirty' ? state.save.since : now);

  return {
    get(): DocumentState {
      return state;
    },

    subscribe(listener: Listener): () => void {
      listeners.add(listener);
      listener(state);
      return () => listeners.delete(listener);
    },

    /** A document read from disk: already saved, dated by its own metadata. */
    opened(doc: ChronoDocument): void {
      resetHistory();
      const at = new Date(doc.meta.savedAt);
      set({
        doc,
        save: { kind: 'opened', at: Number.isNaN(at.getTime()) ? new Date() : at },
        validationErrors: validateDocument(doc),
      });
    },

    /** A document recovered from the session cache keeps its unsaved status. */
    recovered(doc: ChronoDocument, dirty: boolean, since: Date): void {
      resetHistory();
      set({
        doc,
        save: dirty ? { kind: 'dirty', since } : { kind: 'opened', at: new Date(doc.meta.savedAt) },
        validationErrors: validateDocument(doc),
      });
    },

    created(doc: ChronoDocument, now: Date = new Date()): void {
      resetHistory();
      set({ doc, save: { kind: 'never-saved', since: now }, validationErrors: validateDocument(doc) });
    },

    /** Marks a new document state dirty without touching the history. */
    touched(doc: ChronoDocument, now: Date = new Date()): void {
      set({ doc, save: { kind: 'dirty', since: dirtySince(now) }, validationErrors: validateDocument(doc) });
    },

    /** The single entry point for edits: records the previous state for undo. */
    update(next: ChronoDocument, now: Date = new Date()): void {
      if (state.doc) {
        past.push(state.doc);
        if (past.length > MAX_HISTORY) past.shift();
      }
      future = [];
      set({ doc: next, save: { kind: 'dirty', since: dirtySince(now) }, validationErrors: validateDocument(next) });
    },

    undo(now: Date = new Date()): void {
      const previous = past.pop();
      if (!previous || !state.doc) return;

      future.push(state.doc);
      set({ doc: previous, save: { kind: 'dirty', since: dirtySince(now) }, validationErrors: validateDocument(previous) });
    },

    redo(now: Date = new Date()): void {
      const next = future.pop();
      if (!next || !state.doc) return;

      past.push(state.doc);
      set({ doc: next, save: { kind: 'dirty', since: dirtySince(now) }, validationErrors: validateDocument(next) });
    },

    saved(doc: ChronoDocument, now: Date = new Date()): void {
      set({ doc, save: { kind: 'saved', at: now } });
    },

    isDirty(): boolean {
      return state.save.kind === 'dirty' || state.save.kind === 'never-saved';
    },
  };
}

export type DocumentStateStore = ReturnType<typeof createDocumentState>;

export type PillLevel = 'neutral' | 'warn' | 'urgent';

const TIME = new Intl.DateTimeFormat('fr-FR', { hour: '2-digit', minute: '2-digit' });
const DATE = new Intl.DateTimeFormat('fr-FR', { day: '2-digit', month: '2-digit' });

function minutesSince(since: Date, now: Date): number {
  return Math.max(0, Math.floor((now.getTime() - since.getTime()) / 60_000));
}

function elapsed(since: Date, now: Date): { label: string; level: PillLevel } {
  const minutes = minutesSince(since, now);
  const level: PillLevel = minutes >= URGENT_AFTER_MINUTES ? 'urgent' : 'warn';
  return { label: minutes === 0 ? "à l'instant" : `depuis ${minutes} min`, level };
}

/**
 * Deliberately factual wording: under Firefox the app triggers a download but
 * cannot know whether it completed, so the pill reports what happened, never
 * that the work is safe.
 */
export function describeSaveState(save: SaveState, now: Date = new Date()): { text: string; level: PillLevel } {
  switch (save.kind) {
    case 'none':
      return { text: 'Aucun document', level: 'neutral' };
    case 'opened':
      return { text: `Document du ${DATE.format(save.at)} à ${TIME.format(save.at)}`, level: 'neutral' };
    case 'saved':
      return { text: `Enregistré à ${TIME.format(save.at)}`, level: 'neutral' };
    case 'never-saved': {
      const { level } = elapsed(save.since, now);
      return { text: 'Jamais enregistré', level };
    }
    case 'dirty': {
      const { label, level } = elapsed(save.since, now);
      return { text: `Modifié ${label}`, level };
    }
  }
}
